/**
 * Auth store — in-memory JWT with localStorage backup for page reloads.
 */

import jwt_decode from "jwt-decode";
import {
  ACCESS_TOKEN_REFRESH_THRESHOLD_MS,
  IDLE_WARNING_MS,
  SESSION_TYPE,
} from "./constants";
import {
  clearStoredAccessToken,
  writeStoredAccessToken,
} from "./tokenStorage";

const initialState = {
  accessToken: null,
  accessTokenExpiresAt: null,
  /** Interim JWT during 2FA — not a full session; no refresh cookie yet. */
  pendingAccessToken: null,
  isAuthenticated: false,
  isBootstrapping: true,
  /** portal sessions use refresh cookie; admin sessions do not. */
  sessionType: null,
  /** True after a successful /auth/refresh or full portal login with cookie. */
  usesRefreshCookie: false,
  /** Last time the backend refresh cookie was issued/rotated (login or /auth/refresh). */
  sessionExtendedAt: null,
  /** Last mouse, keyboard, scroll, or API activity. */
  lastActivityAt: null,
  /** True while the inactivity dialog is visible — ignore mouse/API session extension. */
  isIdleDialogOpen: false,
};

let state = { ...initialState };
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function getState() {
  return state;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function decodeExpiry(token) {
  try {
    const decoded = jwt_decode(token);
    if (decoded?.exp) {
      return new Date(decoded.exp * 1000).toISOString();
    }
  } catch {
    // ignore decode errors
  }
  return null;
}

/**
 * Full portal or admin session after login, refresh, or 2FA completion.
 * Only portal sessions participate in cookie-based refresh (Phase 2+).
 */
function setAuthState({
  accessToken,
  accessTokenExpiresAt,
  sessionType = SESSION_TYPE.PORTAL,
  usesRefreshCookie,
}) {
  const resolvedUsesRefreshCookie =
    usesRefreshCookie !== undefined
      ? usesRefreshCookie
      : state.usesRefreshCookie;
  const extendsSession =
    Boolean(accessToken) &&
    sessionType === SESSION_TYPE.PORTAL &&
    resolvedUsesRefreshCookie;

  state = {
    ...state,
    accessToken,
    accessTokenExpiresAt: accessTokenExpiresAt ?? decodeExpiry(accessToken),
    pendingAccessToken: null,
    isAuthenticated: Boolean(accessToken),
    sessionType: accessToken ? sessionType : null,
    usesRefreshCookie: resolvedUsesRefreshCookie,
    sessionExtendedAt: extendsSession ? Date.now() : state.sessionExtendedAt,
    lastActivityAt: accessToken ? Date.now() : state.lastActivityAt,
  };

  if (accessToken) {
    writeStoredAccessToken(accessToken);
  }

  emit();
}

/**
 * Interim JWT for 2FA screens. Does not mark the user as authenticated
 * and must not trigger refresh-cookie calls.
 */
function setPendingAccessToken(token) {
  state = {
    ...state,
    pendingAccessToken: token || null,
    isBootstrapping: false,
  };
  emit();
}

function clearPendingAccessToken() {
  if (!state.pendingAccessToken) {
    return;
  }
  state = { ...state, pendingAccessToken: null };
  emit();
}

function setBootstrapping(value) {
  state = { ...state, isBootstrapping: value };
  emit();
}

function setIdleDialogOpen(value) {
  state = { ...state, isIdleDialogOpen: Boolean(value) };
  emit();
}

function clearAuth() {
  clearStoredAccessToken();
  state = { ...initialState, isBootstrapping: false };
  emit();
}

function recordActivity() {
  if (!state.isAuthenticated || state.pendingAccessToken || state.isIdleDialogOpen) {
    return;
  }

  state = { ...state, lastActivityAt: Date.now() };
  emit();
}

function isUserActive() {
  if (state.isIdleDialogOpen || !state.lastActivityAt) {
    return false;
  }

  // Past idle warning → dialog owns the session; do not auto-refresh.
  return Date.now() - state.lastActivityAt < IDLE_WARNING_MS;
}

/** True while the idle popup is open or should be — block interceptor sign-out. */
function isIdleDialogPending() {
  if (state.isIdleDialogOpen) {
    return true;
  }
  if (!state.isAuthenticated || !state.lastActivityAt) {
    return false;
  }
  return Date.now() - state.lastActivityAt >= IDLE_WARNING_MS;
}

/**
 * Token for API Authorization header.
 * Pending 2FA token is included so 2FA API calls can authenticate.
 */
function getAccessToken() {
  return state.accessToken || state.pendingAccessToken || null;
}

function getAuthorizationHeader() {
  const token = getAccessToken();
  if (!token) {
    return null;
  }
  return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
}

function isPortalSession() {
  return state.sessionType === SESSION_TYPE.PORTAL && state.isAuthenticated;
}

function usesRefreshCookie() {
  return state.usesRefreshCookie;
}

function isTokenNearExpiry() {
  if (!state.accessTokenExpiresAt) {
    return true;
  }
  const expiresAt = new Date(state.accessTokenExpiresAt).getTime();
  if (Number.isNaN(expiresAt)) {
    return true;
  }
  return expiresAt - Date.now() < ACCESS_TOKEN_REFRESH_THRESHOLD_MS;
}

export const authStore = {
  getState,
  subscribe,
  setAuthState,
  setPendingAccessToken,
  clearPendingAccessToken,
  setBootstrapping,
  setIdleDialogOpen,
  clearAuth,
  recordActivity,
  isUserActive,
  isIdleDialogPending,
  getAccessToken,
  getAuthorizationHeader,
  isPortalSession,
  usesRefreshCookie,
  isTokenNearExpiry,
};
